<template>
    <div class="rf-view promotions-view shade">
        <button v-if="canShowBell" class="notification-bell-btn" aria-label="Notifications" @click="openNotificationCenter">
            <span class="icon bell" />
            <span v-if="unreadCount > 0" class="notif-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </button>
        <WholesalerLogo :wholesaler="customer.wholesaler" :customer="customer" :customer-number="customerNumber" />
        <main>
            <Spinner v-if="loading && promotions.length == 0" />
            <div v-else class="promotion-grid">
                <PromotionBox v-for="promotion in promotions" :key="promotion.id" :promotion="promotion" :language="customer.settings.language" element-name="button" :selectable="true" @click="openPromotion(promotion)" />
            </div>

            <p v-if="promotions.length == 0" class="style-description">
                {{ $t('promotions.customerEmptyDescription') }}
            </p>
        </main>
    </div>
</template>


<script lang="ts">
import { App as CapApp } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from "@capacitor/push-notifications";
import { FloatingFooter, InputBox, PromotionBox, Spinner, WholesalerLogo, WholesalerTheme } from "@restofrit/components"
import { Session } from "@restofrit/networking";
import { Customer, CustomerNotificationsListResponse, CustomerStatus, Promotion, PromotionCustomer } from "@restofrit/structures";
import { ArrayDecoder, Decoder } from "@simonbackx/simple-encoding";
import { GeneralErrorsView } from "@simonbackx/simple-error-forms";
import { ComponentWithProperties, HistoryManager, NavigationController, NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";

import NotificationCenterView from "../notifications/NotificationCenterView.vue";
import WaitingForVerificationView from "../welcome/WaitingForVerificationView.vue";
import PromotionView from "./PromotionView.vue";

@Component({
    components: {
        InputBox,
        GeneralErrorsView,
        FloatingFooter,
        Spinner,
        PromotionBox,
        WholesalerLogo,
        NotificationCenterView
    },
})
export default class PromotionsView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer: Customer

    @Prop({ required: true })
    customerNumber: number;
    
    loading = false

    promotions: PromotionCustomer[] = []

    delayOpeningId: string | null = null
    listener: PluginListenerHandle | null = null
    pushListener: PluginListenerHandle | null = null

    unreadCount = 0
    canShowBell = false

    mounted() {
        HistoryManager.setUrl("/");
        WholesalerTheme.loadColors(this.customer.wholesaler, this.modalNavigationController!.$el as HTMLElement)

        if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
            PushNotifications.addListener("pushNotificationActionPerformed", (notification: any) => {
                console.log("Tapped push notification")
                console.log(notification)

                // We temporarily use extra AND data because of a bug in capacitor plugin
                // Reference: https://github.com/ionic-team/capacitor-plugins/pull/349
                const data = notification.notification.data ?? notification.notification.extra
                if (data && data.promotionId && typeof data.promotionId === "string") {
                    // Open the promotion
                    this.delayOpeningId = data.promotionId
                    console.log("Set delay opening id "+this.delayOpeningId)

                    if (!this.loading) {
                        this.load().catch(e => console.error(e))
                    }
                }
            }).then(l => {
                this.pushListener = l
            }).catch(console.error)
        }

        // Add live reload to this customer
        CapApp.addListener('appStateChange', ({ isActive }) => {
            console.log('App state changed. Is active?', isActive);
            if (isActive) {
                this.load().catch(e => {
                    console.error(e)
                })
            }
        }).then(l => {
            this.listener = l
        }).catch(console.error);

        


        this.load().catch(e => {
            console.error(e)
        })
    }

    beforeDestroy() {
        if (this.listener) {
            console.log("destroy listener")
            this.listener.remove().catch(console.error)
            this.listener = null
        }
        if (this.pushListener) {
            this.pushListener.remove().catch(console.error)
            this.pushListener = null
        }
    }

    @Watch('customer.settings.status')
    onChangeStatus() {
        console.log("Customer status changed")
        if (this.customer.settings.status != CustomerStatus.Accepted) {
            this.onBlocked()
        }
    }

    openById(promotionId: string) {
        const promotion = this.promotions.find(p => p.id === promotionId)
        if (promotion) {
            console.log("Opening promotion by id "+promotionId)
            this.openPromotion(promotion)
        } else {
            console.error("Didn't find promotion with id "+promotionId)
        }
    }

    onBlocked() {
        this.present({ // Present -> we'll replace the main view in the modal stack component instead of the current navigation controller -> so the tabbar also gets popped away
            components: [
                new ComponentWithProperties(WaitingForVerificationView, {
                    customer: this.customer
                })
            ],
            animated: true,
            replace: 1,
            force: true,
            reverse: true
        })
    }

    async load() {
        this.loading = true

        try {
            // Fetch promotions and the unread notification count in parallel;
            // the notification fetch is non-fatal (bell stays hidden if it fails)
            const [response, notifResponse] = await Promise.all([
                Session.shared.authenticatedServer.request({
                    method: "GET",
                    path: "/customers/"+this.customer.id+"/promotions",
                    decoder: new ArrayDecoder(PromotionCustomer as Decoder<PromotionCustomer>)
                }),
                Session.shared.authenticatedServer.request({
                    method: "GET",
                    path: "/customers/" + this.customer.id + "/notifications",
                    decoder: CustomerNotificationsListResponse as Decoder<CustomerNotificationsListResponse>
                }).catch(() => null)
            ])
            this.promotions = response.data

            if (this.delayOpeningId) {
                this.openById(this.delayOpeningId)
                this.delayOpeningId = null
            }

            if (notifResponse) {
                this.unreadCount = notifResponse.data.unread
                this.canShowBell = true
            }
        } catch (e) {
            console.error(e)
        }

        this.loading = false
    }

    openNotificationCenter() {
        this.show(new ComponentWithProperties(NavigationController, {
            root: new ComponentWithProperties(NotificationCenterView, { customer: this.customer })
        }))
        // Reset local badge count immediately (server-side cleared by NotificationCenterView on mount)
        this.unreadCount = 0
    }

    activated() {
        // Re-fetch unread count when returning from notification center (or any navigation)
        // so the badge reflects any notifications that arrived while away
        Session.shared.authenticatedServer.request({
            method: "GET",
            path: "/customers/" + this.customer.id + "/notifications",
            decoder: CustomerNotificationsListResponse as Decoder<CustomerNotificationsListResponse>
        }).then(response => {
            this.unreadCount = response.data.unread
        }).catch(() => {
            // Non-fatal — badge stays at last known value
        })
    }

    openPromotion(promotion: Promotion) {
        this.present(new ComponentWithProperties(NavigationController, {
            root: new ComponentWithProperties(PromotionView, { promotion, customer: this.customer })
        }))
    }
}
</script>

<style lang="scss">
.promotions-view {
    .notification-bell-btn {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 10;
        background: none;
        border: none;
        cursor: pointer;
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-dark, #050827);
        touch-action: manipulation;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        transition: opacity 0.2s;

        &:active {
            transition: none;
            opacity: 0.4;
        }

        .notif-badge {
            position: absolute;
            top: 2px;
            right: 2px;
            background: #E01919;
            color: white;
            border-radius: 10px;
            min-width: 18px;
            height: 18px;
            font-size: 11px;
            font-weight: bold;
            font-family: Poppins;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 4px;
            box-shadow: 0 0 0 2px var(--color-current-background, #F7F7F7);
        }
    }



    .logo {
        height: 100px;
        display: block;
        margin: 0 auto;
        margin-bottom: 20px;
    }

    .promotion-grid {
        display: grid;
        grid-template-columns: repeat( auto-fit, minmax(250px, 400px) );
        gap: 15px;
        justify-content: center;

        > .promotion-box {
            margin: 0;
        }
    }
}
</style>